import { useState, useRef, useEffect } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Loader2 } from "lucide-react"
import { useToast } from "@/hooks/use-toast"

interface LogoUploadProps {
  logoUrl?: string
  onLogoChange: (file: File | null) => void
  onDeleteLogo?: () => void
  hasExistingLogo?: boolean
  isUploading?: boolean
}

export function LogoUpload({ logoUrl, onLogoChange, onDeleteLogo, hasExistingLogo, isUploading }: LogoUploadProps) {
  const [preview, setPreview] = useState<string | null>(logoUrl || null)
  const [isDragOver, setIsDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { toast } = useToast()

  // Update preview when logoUrl prop changes
  useEffect(() => {
    setPreview(logoUrl || null)
  }, [logoUrl])

  const validateAndProcessFile = (file: File) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid File Type",
        description: "Please select an image file (PNG, JPG, GIF, SVG)",
        variant: "destructive"
      })
      return false
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File Too Large",
        description: "File size must be less than 5MB",
        variant: "destructive"
      })
      return false
    }

    const reader = new FileReader()
    reader.onload = (e) => {
      setPreview(e.target?.result as string)
    }
    reader.readAsDataURL(file)
    onLogoChange(file)
    return true
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      validateAndProcessFile(file)
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!isUploading) {
      setIsDragOver(true)
    }
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)

    if (isUploading) return

    const files = Array.from(e.dataTransfer.files)
    const imageFile = files.find(file => file.type.startsWith('image/'))

    if (imageFile) {
      validateAndProcessFile(imageFile)
    } else if (files.length > 0) {
      toast({
        title: "Invalid File Type",
        description: "Please drop an image file (PNG, JPG, GIF, SVG)",
        variant: "destructive"
      })
    }
  }

  const handleRemoveLogo = () => {
    if (hasExistingLogo && onDeleteLogo) {
      // Delete existing logo from server (with confirmation toast)
      onDeleteLogo()
    } else {
      // Just clear the preview for new upload
      setPreview(null)
      onLogoChange(null)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleButtonClick = () => {
    if (!isUploading) {
      fileInputRef.current?.click()
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Company Logo</CardTitle>
        <CardDescription>
          Upload your company logo for security reports and branding
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-4">
          <div className="flex items-center justify-center w-full">
            <div
              onClick={handleButtonClick}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`flex flex-col items-center justify-center w-full h-64 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                isDragOver
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
              } ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {preview ? (
                <div className="flex flex-col items-center space-y-2">
                  <img
                    src={preview}
                    alt="Logo preview"
                    className="max-w-48 max-h-32 object-contain"
                  />
                  <p className={`text-sm ${isDragOver ? 'text-blue-600 font-semibold' : 'text-gray-500'}`}>
                    {isDragOver ? 'Drop new image to replace' : 'Logo Preview'}
                  </p>
                  {isDragOver && (
                    <p className="text-xs text-blue-500">Click or drop image to replace current logo</p>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <svg
                    className={`w-8 h-8 mb-4 ${isDragOver ? 'text-blue-500' : 'text-gray-500'}`}
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 20 16"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
                    />
                  </svg>
                  <p className={`mb-2 text-sm ${isDragOver ? 'text-blue-600' : 'text-gray-500'}`}>
                    {isDragOver ? (
                      <span className="font-semibold">Drop image here</span>
                    ) : (
                      <>
                        <span className="font-semibold">Click to upload</span> or drag and drop
                      </>
                    )}
                  </p>
                  <p className={`text-xs ${isDragOver ? 'text-blue-500' : 'text-gray-500'}`}>
                    PNG, JPG, GIF or SVG (MAX. 5MB)
                  </p>
                </div>
              )}
            </div>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept="image/*"
            onChange={handleFileSelect}
          />

          <div className="flex gap-2">
            <Button
              onClick={handleButtonClick}
              variant="outline"
              className="flex-1"
              disabled={isUploading}
            >
              {isUploading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isUploading ? 'Uploading...' : (preview ? 'Change Logo' : 'Upload Logo')}
            </Button>
            {(preview || hasExistingLogo) && (
              <Button onClick={handleRemoveLogo} variant="destructive" className="flex-1" disabled={isUploading}>
                {hasExistingLogo ? 'Delete Logo' : 'Remove Logo'}
              </Button>
            )}
          </div>
        </div>

        <div className="text-sm text-gray-500 space-y-1">
          <p><strong>Recommended specifications:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Format: PNG with transparent background preferred</li>
            <li>Resolution: 300x100px or higher</li>
            <li>Aspect ratio: 3:1 (width:height) for best results</li>
            <li>File size: Under 5MB</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  )
}